// Guest-side order tracking for the in-room QR page: polls the public status endpoint and shows received → preparing → delivered.
import React, { useEffect, useState } from 'react';
import { CheckCircle2, ChefHat, Inbox, XCircle } from 'lucide-react';
import { orderStatusText } from '../../pages/inroom_i18n';

const API = process.env.REACT_APP_BACKEND_URL || '';
const KEY = 'lrn_inroom_orders';
const KEEP_MS = 3 * 3600 * 1000;

export const rememberOrder = (id) => {
  try {
    const now = Date.now();
    const list = (JSON.parse(localStorage.getItem(KEY) || '[]')).filter((o) => now - o.at < KEEP_MS && o.id !== id);
    list.unshift({ id, at: now });
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, 5)));
  } catch { /* storage blocked */ }
};

export const recentOrderIds = () => {
  try {
    const now = Date.now();
    return (JSON.parse(localStorage.getItem(KEY) || '[]')).filter((o) => now - o.at < KEEP_MS).map((o) => o.id);
  } catch { return []; }
};

const STEPS = ['new', 'prepared', 'delivered'];
const ICON = { new: Inbox, prepared: ChefHat, delivered: CheckCircle2, cancelled: XCircle };

const useOrderStatus = (orderId) => {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!orderId) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/api/inroom/order/${orderId}/status`);
        if (res.ok) { const j = await res.json(); if (!stop) setSt(j); if (j.status === 'delivered' || j.status === 'cancelled') return; }
      } catch { /* retry on next tick */ }
      if (!stop) timer = setTimeout(tick, 12000);
    };
    let timer = setTimeout(tick, 0);
    return () => { stop = true; clearTimeout(timer); };
  }, [orderId]);
  return st;
};

export const OrderTracker = ({ orderId, lang = 'en', compact = false }) => {
  const st = useOrderStatus(orderId);
  const status = st?.status || 'new';
  const idx = STEPS.indexOf(status);
  const Icon = ICON[status] || Inbox;
  const steps = STEPS.map((s, i) => (
    <span key={s} className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${status === 'cancelled' ? 'bg-red-200' : i <= idx ? 'bg-emerald-500' : 'bg-slate-200'}`} />
  ));
  const summary = (st?.items || []).map((i) => `${i.qty}× ${i.name}`).join(', ');
  return (
    <div className={`rounded-xl border ${status === 'cancelled' ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'} ${compact ? 'p-3' : 'p-4'} text-left`} data-testid={`order-tracker-${orderId}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${status === 'cancelled' ? 'text-red-500' : status === 'delivered' ? 'text-emerald-600' : 'text-emerald-600 animate-pulse'}`} />
        <p className="text-sm font-semibold text-slate-900" data-testid={`order-status-${orderId}`}>{orderStatusText(lang, status)}</p>
      </div>
      <div className="flex gap-1 mt-2">{steps}</div>
      <p className="text-[11px] text-slate-500 mt-1.5">
        {compact && summary ? `${summary} · ` : ''}{status === 'new' || status === 'prepared' ? orderStatusText(lang, 'eta') : ''}
        {st?.handled_at && status !== 'new' ? ` · ${String(st.handled_at).slice(11, 16)}` : ''}
      </p>
    </div>
  );
};

export const RecentOrders = ({ lang, excludeId }) => {
  const ids = recentOrderIds().filter((id) => id !== excludeId);
  if (!ids.length) return null;
  const trackers = ids.map((id) => <OrderTracker key={id} orderId={id} lang={lang} compact />);
  return (
    <div className="mb-4 space-y-2" data-testid="inroom-recent-orders">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{orderStatusText(lang, 'recent')}</p>
      {trackers}
    </div>
  );
};

export default OrderTracker;
